import { frozenBiome } from '../biomeArchetypes';
import { COIL_WORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/coilWorldBackdrops';
import { JOURNEY_DEPTH_BACKDROPS, JOURNEY_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/journeyDepthBackdrops';

/** Brackenmoor — a permanent, moonless blue-green night.
 *
 * The moor needs enough separation to read its path and actors, but it should
 * never drift back toward daylight. Sparse cold motes, restrained drizzle and
 * low fog replace the old pale ceiling/ray stack; a single veiled moon and the
 * last amber lantern make the remaining light feel authored and scarce. */
export const BRACKENMOOR = frozenBiome({
  id: 'brackenmoor',
  sky: { from: 0x141b24, to: 0x2a3438 },
  celestial: {
    atX: 0.72,
    atY: 0.16,
    color: 0xc5d0dc,
    haloColor: 0x718596,
    size: 70,
    haloScale: 3.8,
    coreAlpha: 0.30,
    haloAlpha: 0.10,
  },
  cloudBands: {
    count: 4,
    color: 0x53626a,
    sizeMin: 460,
    sizeMax: 780,
    alphaMin: 0.04,
    alphaMax: 0.10,
    driftPxPerSec: 4,
    bandYMin: 0.08,
    bandYMax: 0.32,
  },
  motes: {
    colors: [0x78958b, 0x91a9a2, 0x5f7f74],
    count: 12,
    sizeMin: 3,
    sizeMax: 8,
    driftPx: 38,
    periodMinMs: 7500,
    periodMaxMs: 15000,
    alphaMin: 0.08,
    alphaMax: 0.28,
  },
  glow: {
    blobs: [
      { color: 0x8296aa, atX: 0.72, atY: 0.16, size: 360, alpha: 0.055 },
      { color: 0xb66c32, atX: 0.12, atY: 0.76, size: 170, alpha: 0.075 },
      { color: 0x3d755e, atX: 0.91, atY: 0.78, size: 230, alpha: 0.06 },
    ],
    breathe: { periodMs: 8500, min: 0.9, max: 1.08 },
  },
  lightField: {
    palette: [0x4f6c65, 0x607c75, 0x344d49],
    count: 7,
    sizeMin: 110,
    sizeMax: 250,
    alphaMin: 0.02,
    alphaMax: 0.07,
    driftRadius: 45,
    periodMinMs: 10000,
    periodMaxMs: 18000,
  },
  weather: {
    mode: 'fall',
    color: 0x71858c,
    colorEnd: 0x465b61,
    size: 3,
    frequencyMs: 115,
    lifespanMs: 2800,
    speed: 28,
    gravityY: 12,
    windX: 38,
    alphaEnd: 0.34,
    gust: { periodMs: 9000, min: 0.7, max: 1.25, phase: 0.3 },
  },
  fog: {
    color: 0x394c4b,
    bands: 4,
    alphaMin: 0.035,
    alphaMax: 0.11,
    driftPx: 42,
    periodMinMs: 12000,
    periodMaxMs: 22000,
    yMin: 0.66,
    yMax: 0.94,
    sizeMin: 240,
    sizeMax: 520,
  },
  parallax: [
    { shape: 'dunes', color: 0x29383c, speed: 0.04, height: JOURNEY_DEPTH_HEIGHTS.brackenmoor, alpha: 0.55, detail: 0.7, sway: { ampPx: 6, periodMs: 34000 }, svg: JOURNEY_DEPTH_BACKDROPS.brackenmoor },
    { shape: 'dunes', color: 0x202d31, speed: 0.10, height: 440, alpha: 0.86, detail: 1.0, svg: COIL_WORLD_BACKDROPS.bracken.mid },
    { shape: 'spires', color: 0x151f23, speed: 0.18, height: 280, alpha: 0.96, detail: 1.1, svg: COIL_WORLD_BACKDROPS.bracken.near },
  ],
  closer: {
    vignette: { color: 0x070d12, edgeAlpha: 0.32, washColor: 0x24363a, washAlpha: 0.035 },
    grade: { top: 0x172331, bottom: 0x1b2b27, strength: 0.16, blend: 'normal' },
  },
});
